import { createMarketTrends } from 'app/shared/models/MarketTrends';
import api, { growthClientWithRequestContext } from 'app/shared/utils/api';
import { RentalGrowthMarketTrendsSummaryWebApiHandler_summary } from '@zg-rentals/ts-rental-growth-api-client';
import { DatarouterError } from '@zg-rentals/web-base';
import { getTopMarketsQuery } from 'app/shared/utils/queries';
import constants from 'app/shared/flux/constants';
import TopMarkets from 'app/shared/models/TopMarkets';
import { isEmpty } from 'app/shared/utils/marketTrendsUtils';
import MedianRentByBedroom from 'app/shared/models/MedianRentByBedroom';

const NON_CRITICAL_ERRORS = ['INVALID_INPUT'];

export const getMarketTrends = (params: {
  resourceId: string;
  bedrooms?: string;
  propertyTypes?: string;
  otherResourceId?: string;
}) => {
  const { resourceId, bedrooms, propertyTypes, otherResourceId } = params;
  // @ts-ignore
  return async (dispatch, getState) => {
    let res = {};
    let response;
    try {
      response = await RentalGrowthMarketTrendsSummaryWebApiHandler_summary(
        {
          resourceId,
          beds: bedrooms,
          propertyTypes,
          rentCompareResourceId: otherResourceId,
        },
        growthClientWithRequestContext(getState()),
      );
    } catch (error) {
      // Non-critical errors (e.g. INVALID_INPUT) leave `response` undefined, which yields empty
      // market trends and triggers the top-markets / 404 fallback below. Re-throw everything else
      // so the SSR/Filter caller can log it and return a 404.
      if (!(DatarouterError.is(error) && NON_CRITICAL_ERRORS.includes(error.code))) {
        throw error;
      }
    }

    const marketTrends = createMarketTrends(response, resourceId, otherResourceId);
    const hasFilters = bedrooms || propertyTypes;
    if (!hasFilters) {
      dispatch({ type: constants.SET_UNFILTERED_MARKET_STATS, payload: marketTrends });
    }
    if (isEmpty(marketTrends)) {
      await dispatch(getTopMarkets(params));
      res = { changeHttpStatus: 404 };
    }

    dispatch({ type: constants.SET_MARKET_STATS, payload: marketTrends });
    return res;
  };
};

export const getUnfilteredMarketTrends = (resourceId: string) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    let response;
    try {
      response = await RentalGrowthMarketTrendsSummaryWebApiHandler_summary(
        { resourceId },
        growthClientWithRequestContext(getState()),
      );
    } catch (error) {
      if (!(DatarouterError.is(error) && NON_CRITICAL_ERRORS.includes(error.code))) {
        throw error;
      }
    }

    dispatch({
      type: constants.SET_UNFILTERED_MARKET_STATS,
      payload: createMarketTrends(response, resourceId),
    });
    return {};
  };
};

export const getTopMarkets = (params: { bedrooms?: string; propertyTypes?: string }) => {
  // @ts-ignore
  return async (dispatch) => {
    const { data, errors } = await dispatch(api.area.graphQlMarketTrends(getTopMarketsQuery(params)));
    if (errors.length > 0) {
      throw new Error(errors[0].code);
    }
    dispatch({
      type: constants.SET_TOP_MARKETS,
      payload: new TopMarkets(data),
    });
    return {};
  };
};

export const getMedianRentByBedroom = (resourceId: string) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    const client = growthClientWithRequestContext(getState());

    // The /summary endpoint returns one market summary per request, so fetch each bed count in
    // parallel. Swallow non-critical errors (e.g. INVALID_INPUT) as a null median; re-throw the
    // rest for the SSR caller to log.
    const fetchMedianRent = async (beds: string): Promise<number | null> => {
      try {
        const response = await RentalGrowthMarketTrendsSummaryWebApiHandler_summary({ resourceId, beds }, client);
        return response?.summary?.medianRent ?? null;
      } catch (error) {
        if (!(DatarouterError.is(error) && NON_CRITICAL_ERRORS.includes(error.code))) {
          throw error;
        }
        return null;
      }
    };

    const [
      studioMedianRent,
      oneBedroomMedianRent,
      twoBedroomMedianRent,
      threeBedroomMedianRent,
      fourPlusBedroomMedianRent,
    ] = await Promise.all([
      fetchMedianRent('0'),
      fetchMedianRent('1'),
      fetchMedianRent('2'),
      fetchMedianRent('3'),
      fetchMedianRent('4plus'),
    ]);

    dispatch({
      type: constants.SET_MEDIAN_RENT_BY_BEDROOM,
      payload: new MedianRentByBedroom({
        studioMedianRent,
        oneBedroomMedianRent,
        twoBedroomMedianRent,
        threeBedroomMedianRent,
        fourPlusBedroomMedianRent,
      }),
    });
    return {};
  };
};
